import * as THREE from 'three';
import type { ModelDocument, ModelEntity, Opening } from '../model/types';
import { computeWallSegments } from '../model/wallGeometry';

export interface ObjectBuildOptions {
  includeEdges?: boolean;
  forExport?: boolean;
}

function makeSurfaceMaterial(entity: ModelEntity): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: entity.color,
    roughness: entity.kind === 'slab' ? 0.88 : 0.72,
    metalness: 0.02,
    transparent: entity.opacity < 0.999,
    opacity: entity.opacity,
    side: THREE.DoubleSide,
  });
}

function makeBoxMesh(
  width: number,
  height: number,
  depth: number,
  material: THREE.Material,
  includeEdges: boolean,
): THREE.Group {
  const group = new THREE.Group();
  const geometry = new THREE.BoxGeometry(
    Math.max(0.0001, width),
    Math.max(0.0001, height),
    Math.max(0.0001, depth),
  );
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);

  if (includeEdges) {
    const edgeGeometry = new THREE.EdgesGeometry(geometry, 28);
    const edges = new THREE.LineSegments(
      edgeGeometry,
      new THREE.LineBasicMaterial({ color: '#34423c', transparent: true, opacity: 0.72 }),
    );
    edges.renderOrder = 4;
    group.add(edges);
  }
  return group;
}

function tagObject(root: THREE.Object3D, entity: ModelEntity): void {
  root.userData.entityId = entity.id;
  root.userData.entityKind = entity.kind;
  root.name = entity.name;
  root.traverse((child) => {
    child.userData.entityId = entity.id;
    child.userData.entityKind = entity.kind;
  });
}

function addOpeningPanel(
  group: THREE.Group,
  opening: Opening,
  wall: ModelEntity,
  includeEdges: boolean,
): void {
  const z = wall.dimensions.depth / 2 + 0.012;
  const y = opening.sill + opening.height / 2 - wall.dimensions.height / 2;

  if (opening.kind === 'door') {
    const panel = makeBoxMesh(
      Math.max(0.08, opening.width * 0.92),
      Math.max(0.08, opening.height * 0.96),
      0.035,
      new THREE.MeshStandardMaterial({ color: opening.color, roughness: 0.58, metalness: 0.02 }),
      includeEdges,
    );
    panel.position.set(opening.center, y, z);
    panel.userData.openingId = opening.id;
    group.add(panel);

    const handle = new THREE.Mesh(
      new THREE.SphereGeometry(0.025, 12, 8),
      new THREE.MeshStandardMaterial({ color: '#d9b56f', metalness: 0.7, roughness: 0.22 }),
    );
    handle.position.set(opening.center + opening.width * 0.31, y, z + 0.04);
    group.add(handle);
    return;
  }

  const glass = makeBoxMesh(
    Math.max(0.08, opening.width * 0.92),
    Math.max(0.08, opening.height * 0.9),
    0.018,
    new THREE.MeshPhysicalMaterial({
      color: opening.color,
      roughness: 0.15,
      metalness: 0,
      transparent: true,
      opacity: 0.48,
      transmission: 0.18,
      side: THREE.DoubleSide,
    }),
    false,
  );
  glass.position.set(opening.center, y, z);
  glass.userData.openingId = opening.id;
  group.add(glass);

  const frameMaterial = new THREE.MeshStandardMaterial({ color: '#30423b', roughness: 0.46, metalness: 0.2 });
  const frameThickness = Math.min(0.055, opening.width * 0.08, opening.height * 0.08);
  const frameDepth = 0.045;
  const framePieces: Array<{ width: number; height: number; x: number; y: number }> = [
    { width: opening.width, height: frameThickness, x: 0, y: opening.height / 2 - frameThickness / 2 },
    { width: opening.width, height: frameThickness, x: 0, y: -opening.height / 2 + frameThickness / 2 },
    { width: frameThickness, height: opening.height, x: -opening.width / 2 + frameThickness / 2, y: 0 },
    { width: frameThickness, height: opening.height, x: opening.width / 2 - frameThickness / 2, y: 0 },
    { width: frameThickness * 0.7, height: opening.height, x: 0, y: 0 },
  ];
  for (const piece of framePieces) {
    const frame = makeBoxMesh(piece.width, piece.height, frameDepth, frameMaterial, false);
    frame.position.set(opening.center + piece.x, y + piece.y, z + 0.014);
    group.add(frame);
  }
}

export function createEntityObject(entity: ModelEntity, options: ObjectBuildOptions = {}): THREE.Object3D {
  const includeEdges = options.includeEdges ?? true;
  const surface = makeSurfaceMaterial(entity);
  let root: THREE.Object3D;

  if (entity.kind === 'line') {
    const points = [
      new THREE.Vector3(-entity.dimensions.width / 2, 0, 0),
      new THREE.Vector3(entity.dimensions.width / 2, 0, 0),
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    root = new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: entity.color, linewidth: 1 }));
  } else if (entity.kind === 'wall') {
    const wallGroup = new THREE.Group();
    for (const segment of computeWallSegments(entity)) {
      const part = makeBoxMesh(segment.width, segment.height, entity.dimensions.depth, surface, includeEdges);
      part.position.set(segment.x, segment.y - entity.dimensions.height / 2, 0);
      wallGroup.add(part);
    }
    for (const opening of entity.openings ?? []) addOpeningPanel(wallGroup, opening, entity, includeEdges);
    root = wallGroup;
  } else {
    root = makeBoxMesh(
      entity.dimensions.width,
      entity.dimensions.height,
      entity.dimensions.depth,
      surface,
      includeEdges,
    );
  }

  root.position.set(...entity.position);
  root.rotation.set(...entity.rotation);
  root.scale.set(...entity.scale);
  root.visible = entity.visible;
  tagObject(root, entity);
  return root;
}

export function createDocumentRoot(document: ModelDocument, options: ObjectBuildOptions = {}): THREE.Group {
  const root = new THREE.Group();
  root.name = document.name;
  const layerMap = new Map(document.layers.map((layer) => [layer.id, layer]));
  for (const entity of document.entities) {
    const layer = layerMap.get(entity.layerId);
    if (!entity.visible || layer?.visible === false) continue;
    root.add(createEntityObject(entity, options));
  }
  return root;
}

export function disposeObject(root: THREE.Object3D): void {
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = (mesh as THREE.Mesh).material;
    if (Array.isArray(material)) material.forEach((item) => item.dispose());
    else if (material && 'dispose' in material) material.dispose();
  });
}
